// Pure pane-content helpers for the compare view (RES-561) — no React imports so
// they stay unit-testable without dragging the editor stack into jest.

import { PX_PER_PT } from '../../FlowingPage/pageLayoutMath';

// Content components with a Raw (context-free, load-prop) variant — the diffable
// set. Other editor-component-slate consumers fall through to the live-component
// path (not diffed) until they grow a Raw wrapper. ComparePane maps these names to
// the actual components.
export const SLATE_RAW_COMPONENT_NAMES = ['EditableText', 'FinalisedEditableText', 'SlateStaticData'];

// Layout-only markers that mean nothing in a single scrolled pane.
export const SKIPPED_COMPONENTS = ['PageBreak'];

// Decorative/static components that render template chrome, not fund data — they
// still render live but a "Live data — not compared" badge on every divider and
// label drowns the page (44 badges on the Flash Note template). Anything not
// listed here keeps the badge: charts and data tables are the point of it.
export const UNBADGED_COMPONENTS = [
	'Divider', 'Image', 'ClippedImage', 'FixedLabel', 'EditableLabel',
	'FootNoteableFixedLabel', 'Pill', 'FlowingPageFooter',
];

export function showsLiveBadge(className) {
	return !UNBADGED_COMPONENTS.includes(className);
}

// The exact prop set ComparePane's live-component path spreads onto every non-Raw
// content component (each block adds its own `config`). The render-gate test
// (liveBlockRenderGate.test.js) consumes this SAME function, so the gate always
// exercises the real contract — extend it here and the gate tests the new shape
// automatically. pageSize is the block's owning page's size, matching the editor
// (AbsoluteComponent injects pageSize={page.size}); components like
// MultiColumnOrderedList read pageSize.width unconditionally and crash without it
// (the RES-561 CFS Bulletin bug). The editor's Page.js also injects portfolio
// state the compare view doesn't have; the null getters keep those components on
// their empty paths.
export function livePaneProps(templateConfig, pageSize) {
	return {
		styles: templateConfig.styles,
		datasets: templateConfig.datasets,
		pageSize,
		getPortfoliosState: () => null,
		getPortfoliosGroupedState: () => null,
		getCurrentFundNumber: () => null,
		getPasteGroupings: () => null,
		getAllFunds: () => null,
	};
}

// Node-range segments for a FLOWING page's body, from the side's persisted
// `<dataName>PageBreaks` key (auto + manual breaks the editor saves on every
// relayout — follow-up item 6). Each break's `at` path names the top-level node
// the next rendered sheet starts at; the segment count then matches the editor's
// footer "1 / N" for that save. Absent/unparseable keys, breaks without a usable
// `at`, or a body that isn't a Slate array fall back to one whole-body segment
// (the pre-item-6 behaviour).
export function flowSegmentRanges(contentMap, dataName, nodeCount) {
	const whole = [{ from: 0, to: nodeCount }];
	if (!contentMap || !nodeCount)
		return whole;
	const raw = contentMap[dataName + 'PageBreaks'];
	if (!raw || typeof raw !== 'string')
		return whole;
	let breaks;
	try {
		breaks = JSON.parse(raw);
	} catch (e) {
		return whole;
	}
	if (!Array.isArray(breaks))
		return whole;
	const indices = [...new Set(
		breaks
			.map((b) => (b && Array.isArray(b.at) ? b.at[0] : null))
			.filter((i) => Number.isInteger(i) && i > 0 && i < nodeCount)
	)].sort((a, b) => a - b);
	const bounds = [0, ...indices, nodeCount];
	return bounds.slice(0, -1).map((from, i) => ({ from, to: bounds[i + 1] }));
}

// Layout pages (2A, plan-compareDocuments.md §3.12 A): FIXED/RELATIVE template pages
// render as true laid-out sheets — pt-sized page, absolutely positioned blocks —
// mirroring the editor's Page.js/AbsoluteComponent and therefore the published PDF.
export function isLayoutPage(page) {
	return page.type === 'FIXED' || page.type === 'RELATIVE';
}

// Per-page number tokens (3A, plan-compareDocuments.md §3.12 B): the PDF evaluates
// ${PAGE_NUM} / ${PAGE_NUM_OFFSET(n)} at draw time as the current page count
// (PDFBuilder.getValue), so on compare they resolve to the emitted page's 1-based
// ordinal. Resolved at collect time — the editor's DataController keeps its
// PAGE_NUM → "" short-circuit untouched (a resolved config has no token left).
export function substitutePageTokens(value, ordinal) {
	if (typeof value !== 'string' || !value.includes('PAGE_NUM'))
		return value;
	return value
		.replace(/\$\{PAGE_NUM\}/g, String(ordinal))
		.replace(/\$\{PAGE_NUM_OFFSET\((-?\d+)\)\}/g, (match, offset) => String(ordinal + parseInt(offset, 10)));
}

// Deep-substitute page tokens through a block configuration (they sit at varying
// depths: FixedLabel's value.value, FlowingPageFooter's pageValue). Returns the
// ORIGINAL object when nothing matched so untouched configs keep their identity.
export function substituteConfigPageTokens(node, ordinal) {
	if (typeof node === 'string')
		return substitutePageTokens(node, ordinal);
	if (Array.isArray(node)) {
		let changed = false;
		const next = node.map((item) => {
			const sub = substituteConfigPageTokens(item, ordinal);
			if (sub !== item)
				changed = true;
			return sub;
		});
		return changed ? next : node;
	}
	if (node && typeof node === 'object') {
		let changed = false;
		const next = {};
		Object.keys(node).forEach((key) => {
			const sub = substituteConfigPageTokens(node[key], ordinal);
			if (sub !== node[key])
				changed = true;
			next[key] = sub;
		});
		return changed ? next : node;
	}
	return node;
}

// Which flow segments an overlay repeats on — exact mirror of the PDF's
// shouldShowComponentInFlowingPage (PDFBuilder.java): absent key = every page;
// first/rest/last as named (a single-segment flow is both first and last);
// any other value = never (the Java falls through to false).
export function showsOnFlowSegment(configuration, isFirst, isLast) {
	const mode = configuration ? configuration.flowingShowOnPages : undefined;
	if (mode === undefined || mode === null)
		return true;
	const normalised = String(mode).toLowerCase();
	if (normalised === 'first')
		return isFirst;
	if (normalised === 'rest')
		return !isFirst;
	if (normalised === 'last')
		return isLast;
	return false;
}

// The margins box for one flow segment — FlowingPage.js's exact interpretation:
// width always comes from the base margins; firstPageMargins only moves the first
// page's top edge, lastPageMargins the last page's bottom (a single segment can
// take both). Missing margins degrade to zero (body at the page origin).
export function flowSegmentMargins(flowContent, isFirst, isLast) {
	const base = flowContent.margins || { top: 0, right: 0, bottom: 0, left: 0 };
	const first = isFirst && flowContent.firstPageMargins ? flowContent.firstPageMargins : null;
	const last = isLast && flowContent.lastPageMargins ? flowContent.lastPageMargins : null;
	return {
		top: first ? first.top : base.top,
		bottom: last ? last.bottom : base.bottom,
		left: base.left,
		right: base.right,
	};
}

// Points → CSS px — re-exported from the editor's single conversion constant
// (FlowingPage/pageLayoutMath.js) so the sheet scale can never drift from the
// editor's own pt↔px assumption.
export const PT_TO_PX = PX_PER_PT;

// Scale that fits a pageWidthPt-wide sheet into containerWidthPx, capped at
// natural size (a pane wider than the sheet shows it 1:1, like the editor).
export function sheetScale(containerWidthPx, pageWidthPt) {
	if (!containerWidthPx || !pageWidthPt)
		return 1;
	return Math.min(1, containerWidthPx / (pageWidthPt * PT_TO_PX));
}

// Page background: direct colour wins, else a backgroundStyle reference into the
// template styles (same resolution as the editor's Page.js).
export function resolveSheetBackground(layout, templateStyles) {
	if (!layout)
		return null;
	if (layout.backgroundColor)
		return layout.backgroundColor;
	if (layout.backgroundStyle && templateStyles && templateStyles[layout.backgroundStyle])
		return templateStyles[layout.backgroundStyle].backgroundColor || null;
	return null;
}

// Group the template into pages of ordered blocks. FIXED/RELATIVE pages carry a
// `layout` descriptor and keep TEMPLATE order — that is the editor's/PDF's paint
// order (a full-page background Divider is authored first so everything else
// draws on top of it); their blocks keep `location` for absolute positioning.
// Empty pages still get an entry so page numbering matches the editor's page count.
//
// With a side's contentMap (follow-up item 6) a FLOWING page splits into one page
// entry per stored page break — each rendered as its own .compare-pane-page so
// the rail's scroll-spy/jump follow automatically. Without a contentMap
// (collectDiffableKeys and older callers) the flow body stays one page. pageIdx is
// the sequential RENDER index — with splits it no longer matches the template
// page index.
//
// 3A (plan-compareDocuments.md §3.12 B): a FLOWING page with a size renders like the
// PDF — each segment is a pt sheet (layout carries flow: true so the sheet may
// overflow instead of clipping), the body sits in the margins box, and the
// overlays repeat on every segment at their template position, filtered by
// flowingShowOnPages. A sizeless flow page keeps the flat reading-order stack
// with overlays flattened onto the first segment. Every emitted page also gets a
// 1-based `ordinal` and has ${PAGE_NUM}-family tokens in its block configs
// resolved against it, matching the PDF's per-page evaluation.
export function collectPages(templateConfig, contentMap) {
	const pages = [];
	const pushPage = (blocks, layout, templateIdx) => {
		const ordinal = pages.length + 1;
		pages.push({
			pageIdx: pages.length,
			// The template page this rendered page came from — flow splits emit
			// several pages sharing one templateIdx. It is the only page identity
			// stable ACROSS the two panes (pageIdx diverges after a split), so the
			// scroll sync anchors on it (plan-compareDocuments.md §3.12 C).
			templateIdx,
			ordinal,
			blocks: blocks.map((block) => {
				const configuration = substituteConfigPageTokens(block.configuration, ordinal);
				return configuration === block.configuration ? block : { ...block, configuration };
			}),
			layout,
		});
	};
	(templateConfig.pages || []).forEach((page, templateIdx) => {
		// A layout page must have a size — without one there is no reference frame
		// to position against, so it falls back to the flat reading-order stack.
		const layout = isLayoutPage(page) && page.size
			? {
				size: page.size,
				backgroundColor: page.backgroundColor || null,
				backgroundStyle: page.backgroundStyle || null,
			}
			: null;
		const flowing = page.type === 'FLOWING' && page.flowContent && page.flowContent.dataName;
		// Sized flow pages position their overlays too (3A) — same origin-anchoring
		// rule as layout pages.
		const positioned = !!layout || !!(flowing && page.size);
		const overlays = [];
		const ordered = (page.content || []).map((content, idx) => ({ content, idx }));
		if (!positioned)
			ordered.sort((a, b) => {
				const at = a.content.location ? a.content.location.top : 0;
				const bt = b.content.location ? b.content.location.top : 0;
				const al = a.content.location ? a.content.location.left : 0;
				const bl = b.content.location ? b.content.location.left : 0;
				return at - bt || al - bl || a.idx - b.idx;
			});
		ordered.forEach(({ content, idx }) => {
			if (SKIPPED_COMPONENTS.includes(content.className))
				return;
			overlays.push({
				key: `p${templateIdx}-c${idx}`,
				className: content.className,
				configuration: content.configuration,
				pageSize: page.size || null,
				// Every block on a sheet gets a position — a template record with no
				// location at all anchors at the page origin (the editor would crash
				// on it; the sheet must not let it reflow in-flow).
				location: positioned
					? { left: content.location?.left || 0, top: content.location?.top || 0 }
					: null,
			});
		});
		if (flowing) {
			const dataName = page.flowContent.dataName;
			const nodes = parseSlateValue(contentMap ? contentMap[dataName] : null);
			const ranges = flowSegmentRanges(contentMap, dataName, nodes ? nodes.length : 0);
			const flowLayout = page.size
				? {
					size: page.size,
					backgroundColor: page.backgroundColor || null,
					backgroundStyle: page.backgroundStyle || null,
					flow: true,
				}
				: null;
			ranges.forEach((segment, segIdx) => {
				const isFirst = segIdx === 0;
				const isLast = segIdx === ranges.length - 1;
				const margins = flowLayout ? flowSegmentMargins(page.flowContent, isFirst, isLast) : null;
				const blocks = [{
					key: `p${templateIdx}-flow` + (ranges.length > 1 ? `-s${segIdx}` : ''),
					className: 'EditableText',
					configuration: page.flowContent,
					pageSize: page.size || null,
					location: margins ? { left: margins.left, top: margins.top } : null,
					// The body's measure on the sheet — same maths as FlowingPage.js.
					width: margins ? page.size.width - margins.left - margins.right : null,
					// null segment = render the whole value (no slicing).
					segment: ranges.length > 1 ? segment : null,
				}];
				if (flowLayout)
					blocks.push(...overlays
						.filter((overlay) => showsOnFlowSegment(overlay.configuration, isFirst, isLast))
						.map((overlay) => (ranges.length > 1 ? { ...overlay, key: overlay.key + '-s' + segIdx } : overlay)));
				else if (segIdx === 0)
					blocks.push(...overlays);
				pushPage(blocks, flowLayout, templateIdx);
			});
			return;
		}
		pushPage(overlays, layout, templateIdx);
	});
	return pages;
}

// Flatten the template into an ordered block list.
export function collectBlocks(templateConfig) {
	return collectPages(templateConfig).flatMap((page) => page.blocks);
}

// The content keys the diff engine should compare: only keys rendered through a Raw
// Slate component are annotated, so diffing anything else would produce hunks the
// panes could never display.
export function collectDiffableKeys(templateConfig) {
	return [...new Set(
		collectBlocks(templateConfig)
			.filter((b) => SLATE_RAW_COMPONENT_NAMES.includes(b.className) && b.configuration && b.configuration.dataName)
			.map((b) => b.configuration.dataName)
	)];
}

// The content keys that actually have a block able to DISPLAY a diff mark in one of
// the two panes — i.e. a Raw Slate block, mirroring CompareBlock's own condition. A
// key rendered only by a non-Slate component could never show an annotation, so it
// does not count as "shown".
function renderedDiffableKeys(...pageLists) {
	const keys = new Set();
	pageLists.forEach((pages) => (pages || []).forEach((page) => (page.blocks || []).forEach((block) => {
		if (SLATE_RAW_COMPONENT_NAMES.includes(block.className) && block.configuration && block.configuration.dataName)
			keys.add(block.configuration.dataName);
	})));
	return keys;
}

// Changed content keys that NO block in either pane renders — the compare-correctness
// hole found by the 2026-08-07 CFS Investments run (defect 1). The page skeleton comes
// from `page-expansion`, which strips conditional pages and blocks against TODAY's fund
// data, so a page that existed at publish time can be absent from both panes and take
// its diff marks with it. Diffing the UNEXPANDED template's key set (ComparePage passes
// the full template to diffContentMap) means the change is still computed; this is what
// lets the UI say so instead of dropping it silently.
//
// 'incomparable' is deliberately excluded: at least one side failed to parse, so no
// change is known — claiming one for a key the reviewer cannot even see would be a
// false alarm. Rendered incomparable keys already say so in place.
export function hiddenChangedKeys(diff, leftPages, rightPages) {
	if (!diff || !diff.keys)
		return [];
	const rendered = renderedDiffableKeys(leftPages, rightPages);
	return Object.keys(diff.keys).filter((key) => {
		const entry = diff.keys[key];
		return !!entry && entry.status !== 'unchanged' && entry.status !== 'incomparable' && !rendered.has(key);
	});
}

// Reader-facing label for a content key — the template stores no label alongside
// dataName, so split its camel/Pascal case: 'TickerCommentary' → 'Ticker Commentary',
// 'CoBrandPageBody' → 'Co Brand Page Body'. Runs of capitals stay together ('PDSName'
// → 'PDS Name').
export function humaniseDataName(dataName) {
	if (!dataName)
		return '';
	return String(dataName)
		.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
		.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
		.trim();
}

export function parseSlateValue(raw) {
	if (!raw)
		return null;
	try {
		const parsed = JSON.parse(raw);
		return Array.isArray(parsed) ? parsed : null;
	} catch (e) {
		return null;
	}
}

// Picks what a Slate block should render on one side. diffEntry is
// diffContentMap's per-key result (undefined while the other side is loading).
// Returns {value, status?, incomparable?}: value is the node array to load (null →
// render nothing), status stamps the block wrapper class for whole-key changes.
export function resolveBlockValue(diffEntry, side, raw) {
	if (!diffEntry)
		return { value: parseSlateValue(raw) };
	if (diffEntry.status === 'incomparable')
		// Legacy/scalar format on at least one side — show this side plain (when it
		// does parse) under an explicit notice, never annotated and never a crash.
		return { value: parseSlateValue(raw), status: 'incomparable', incomparable: true };
	// The diff layer's "right" is its NEW side; the newer document renders in the
	// LEFT pane (follow-up item 3) — ComparePage feeds diffContentMap(right, left)
	// to match. Left pane shows diffAdded marks, right pane diffRemoved.
	const annotated = side === 'left' ? diffEntry.rightAnnotated : diffEntry.leftAnnotated;
	return { value: annotated || parseSlateValue(raw), status: diffEntry.status };
}

// Map a scrollTop through the two panes' template-page segments, so the panes
// re-align at every template-page boundary instead of drifting from the first
// pixel whenever their total heights differ (plan-compareDocuments.md §3.12 C).
// Spans are readSegmentSpans output — ordered, contiguous, one per templateIdx.
// Walks the source spans keeping a target-side cursor: a matched span maps its
// in-segment fraction onto [cursor, match.end]. The cursor makes the map cover
// ALL target content: template pages the target renders but the source doesn't
// leave the cursor behind at their start, so their body is scrolled through as
// part of the next matched segment rather than teleported past in one frame
// (review finding). A span missing on the TARGET side maps onto the empty
// interval at the cursor — the boundary where it would have sat. Returns null
// when either side has no spans (loading, legacy caller) — the caller falls
// back to the proportional map.
export function mapScrollPosition(pos, sourceSpans, targetSpans) {
	if (!sourceSpans?.length || !targetSpans?.length)
		return null;
	// Overscroll (macOS rubber-banding) reports a negative scrollTop; clamping
	// here keeps the leading-padding division below well-defined.
	pos = Math.max(0, pos);
	const first = sourceSpans[0];
	const targetFirst = targetSpans[0];
	// Above the first segment (the pane body's leading padding): map linearly
	// between the two paddings so the approach into page 1 stays smooth.
	if (pos < first.top)
		return (pos / first.top) * targetFirst.top;
	let cursor = targetFirst.top;
	let result = cursor;
	for (const span of sourceSpans) {
		if (span.top > pos)
			break;
		const match = targetSpans.find((t) => t.templateIdx === span.templateIdx);
		if (!match) {
			// Stripped on the target side (the hiddenChanged case): pin to the
			// boundary where the page would have sat.
			targetSpans.forEach((t) => {
				if (t.templateIdx < span.templateIdx)
					cursor = Math.max(cursor, t.end);
			});
			result = cursor;
			continue;
		}
		const start = cursor;
		const end = Math.max(start, match.end);
		const size = span.end - span.top;
		const fraction = size > 0 ? Math.min(1, (pos - span.top) / size) : 0;
		result = start + fraction * (end - start);
		cursor = end;
	}
	return result;
}

// Shared geometry read for the scroll handler: one getBoundingClientRect pass
// over the page wrappers serves both the scroll-spy (tops) and the sync (spans)
// — they used to measure the same pane twice per scroll event (review finding).
export function readPageGeometry(bodyEl) {
	const bodyRect = bodyEl.getBoundingClientRect();
	return Array.from(bodyEl.querySelectorAll('.compare-pane-page')).map((pageEl) => ({
		top: pageEl.getBoundingClientRect().top - bodyRect.top + bodyEl.scrollTop,
		templateIdx: Number(pageEl.dataset?.templateIdx),
	}));
}

// One span per template page: where it starts in the pane body's scroll space and
// where the next one takes over (contiguous — every scrollTop belongs to exactly
// one span; the last one runs to scrollHeight). Split flow segments share their
// template page's span. Pure math over readPageGeometry output so it stays
// testable with plain objects.
export function segmentSpans(geometry, scrollHeight) {
	const spans = [];
	geometry.forEach(({ top, templateIdx }) => {
		if (Number.isNaN(templateIdx))
			return;
		if (!spans.length || spans[spans.length - 1].templateIdx !== templateIdx)
			spans.push({ templateIdx, top, end: top });
	});
	spans.forEach((span, i) => {
		span.end = i + 1 < spans.length ? spans[i + 1].top : scrollHeight;
	});
	return spans;
}

export function readSegmentSpans(bodyEl) {
	return segmentSpans(readPageGeometry(bodyEl), bodyEl.scrollHeight);
}

// For mapping between scroll POSITIONS the last span must end at the pane's
// maximum scrollTop, not at scrollHeight: content coordinates would leave a
// source at its bottom clamp mapping the target short of ITS bottom whenever the
// final segments' heights differ — the tails could then never align (review
// finding). Guarded against a last page shorter than the viewport (top > range).
function scrollableSpans(spans, range) {
	if (!spans?.length)
		return spans;
	const last = spans[spans.length - 1];
	const end = Math.max(last.top + 1, range);
	return end === last.end ? spans : [...spans.slice(0, -1), { ...last, end }];
}

// Scroll sync between the two pane bodies: segment-anchored via mapScrollPosition
// when both sides' spans are known, proportional across the whole range otherwise.
// A source pinned at its bottom clamp always maps to the target's bottom, so the
// documents end together even when scrollableSpans' rewrite can't guarantee it.
// Returns whether it actually moved the target — the caller's feedback-loop guard
// must only arm when a write happened (an equal scrollTop fires no scroll event,
// so the guard would otherwise swallow the next genuine scroll). The write is
// pre-clamped to the target's real range for the same reason: the browser clamps
// an overshooting assignment silently, which would report a write that never fired
// a scroll event. Pure DOM math so it stays trivially testable.
export function syncScrollTop(source, target, sourceSpans, targetSpans) {
	const sourceRange = source.scrollHeight - source.clientHeight;
	const targetRange = target.scrollHeight - target.clientHeight;
	if (sourceRange <= 0 || targetRange <= 0)
		return false;
	const mapped = mapScrollPosition(
		source.scrollTop,
		scrollableSpans(sourceSpans, sourceRange),
		scrollableSpans(targetSpans, targetRange)
	);
	let next = mapped !== null ? mapped : (source.scrollTop / sourceRange) * targetRange;
	if (source.scrollTop >= sourceRange - 1)
		next = targetRange;
	next = Math.max(0, Math.min(targetRange, next));
	if (Math.abs(next - target.scrollTop) <= 1)
		return false;
	target.scrollTop = next;
	return true;
}

// The scrollTop at which each of the pane body's page wrappers sits flush with the
// top of the viewport. Rect-based rather than offsetTop: the pane body is not a
// positioned ancestor of the wrappers, so offsetTop would measure against the wrong
// box. The page math lives in currentPageIndex so it stays testable with plain
// objects; callers that also need spans should read geometry once and derive both.
export function readPageTops(bodyEl) {
	return readPageGeometry(bodyEl).map((page) => page.top);
}

// Scroll-spy: which page is "current" for a pane scrolled to scrollTop — page i
// takes over once its top has crossed the activation line, 40% of the viewport
// down (so a page counts once it dominates the view, not the instant its first
// pixel appears). ONLY when the previous page is itself shorter than that line
// does the line drop to half that page's height: a shorter-than-40% page could
// never hold the next top above a fixed line while sitting flush at the top, so
// the counter used to skip it and read the wrong number after a page-nav jump
// onto it (the 2026-08-07 stress-run off-by-one; plan-compareDocuments.md §3.12 C
// companion). Pages at or above the 40% line keep the old fixed-line behaviour
// exactly — the fix is scoped to the defect class (review finding). Scrolled
// within a pixel of the bottom clamps to the last page: a short final page may
// never reach the top edge, but the counter must still be able to read N / N.
export function currentPageIndex({ scrollTop, clientHeight, scrollHeight }, pageTops) {
	if (!pageTops.length)
		return 0;
	if (scrollTop + clientHeight >= scrollHeight - 1)
		return pageTops.length - 1;
	const line = clientHeight * 0.4;
	let current = 0;
	for (let i = 1; i < pageTops.length; i++) {
		const previousSpan = pageTops[i] - pageTops[i - 1];
		const activation = previousSpan < line ? previousSpan * 0.5 : line;
		// If page i hasn't activated, later pages can't have either (tops ascend
		// and activation grows by at most the distance to the next top).
		if (pageTops[i] > scrollTop + activation)
			break;
		current = i;
	}
	return current;
}
